import { Component } from '@theme/component';
import { morph } from '@theme/morph';
import { ThemeEvents } from '@theme/events';

/**
 * <back-in-stock-optin>
 *
 * Lets a shopper opt in to a one-time "back in stock" email on a sold-out product.
 * Wraps the theme's reusable <dialog-component> and a native {% form 'contact' %}; the
 * opt-in is emailed to the store's Sender inbox (zero backend). The owner notifies people manually.
 *
 * Notes on the theme's component model (verified against component.js):
 *  - The trigger / dialog / form all live inside <dialog-component>, so their `ref` attributes
 *    bind to dialog-component, NOT to this element. We therefore use querySelector(), not this.refs.
 *  - The submit listener is attached on the component element itself: submit events bubble, and the
 *    element survives morph() — so the handler keeps working after a variant-driven re-render.
 */

const OPTIN_PREFIX = 'bis_optin_';
const PENDING_VARIANT_KEY = 'bis_pending_variant';
const PENDING_EMAIL_KEY = 'bis_pending_email';

// The per-variant opt-in flag is soft dedup only; expire it after 1 week so the trigger resets
// to "Obvestite me" (the shopper may want to be reminded again on a later restock cycle).
const OPTIN_TTL_MS = 7 * 24 * 60 * 60 * 1000;

const TRIGGER_LABEL_DEFAULT = 'Obvestite me, ko bo spet na zalogi';
const TRIGGER_LABEL_DONE = 'Ste na seznamu';

class BackInStockOptin extends Component {
  connectedCallback() {
    super.connectedCallback();

    // Submit interception — on the element (bubbling + morph-safe), not on the form node.
    this.addEventListener('submit', this.#handleSubmit);

    // Variant reactivity — mirrors local-pickup.js. The catalog is single-variant today, so this is
    // effectively inert, but it keeps the component correct if a product ever gains a sold-out sibling.
    const closestSection = this.closest('.shopify-section, dialog');
    closestSection?.addEventListener(ThemeEvents.variantUpdate, this.#onVariantUpdate);

    this.disconnectedCallback = () => {
      closestSection?.removeEventListener(ThemeEvents.variantUpdate, this.#onVariantUpdate);
    };

    // If we just came back from a native contact-form submit (return_to -> ?bis_ok=1), confirm it.
    this.#handleReturn();

    // Reflect any prior opt-in for the current variant.
    this.#applyOptInState();
  }

  get #variantId() {
    return this.dataset.variantId || '';
  }

  /** Flip the trigger between "Obvestite me…" and the disabled "Ste na seznamu" state. */
  #applyOptInState = () => {
    const trigger = this.querySelector('.back-in-stock__trigger');
    if (!trigger) return;

    const opted = this.#isOptedIn(this.#variantId);
    trigger.classList.toggle('back-in-stock__trigger--done', opted);
    trigger.disabled = opted;

    const label = trigger.querySelector('.back-in-stock__trigger-label');
    if (label) label.textContent = opted ? TRIGGER_LABEL_DONE : TRIGGER_LABEL_DEFAULT;

    const bell = trigger.querySelector('.back-in-stock__trigger-icon--bell');
    const check = trigger.querySelector('.back-in-stock__trigger-icon--check');
    if (bell) bell.hidden = opted;
    if (check) check.hidden = !opted;
  };

  /** @param {string} variantId — true only if opted in within the last OPTIN_TTL_MS (else self-cleans). */
  #isOptedIn = (variantId) => {
    try {
      const raw = localStorage.getItem(OPTIN_PREFIX + variantId);
      if (!raw) return false;
      const expiresAt = parseInt(raw, 10);
      if (!Number.isFinite(expiresAt) || Date.now() > expiresAt) {
        localStorage.removeItem(OPTIN_PREFIX + variantId);
        return false;
      }
      return true;
    } catch (_e) {
      return false;
    }
  };

  /** @param {string} variantId — store the expiry timestamp (now + 1 week). */
  #rememberOptIn = (variantId) => {
    try {
      localStorage.setItem(OPTIN_PREFIX + variantId, String(Date.now() + OPTIN_TTL_MS));
    } catch (_e) {
      // private mode / storage disabled — soft dedup only, acceptable (sending is manual).
    }
  };

  /** @param {string} message */
  #showError = (message) => {
    const error = this.querySelector('.back-in-stock__error');
    if (!error) return;
    error.textContent = message;
    error.hidden = false;
  };

  #clearError = () => {
    const error = this.querySelector('.back-in-stock__error');
    if (!error) return;
    error.textContent = '';
    error.hidden = true;
  };

  /** Swap the modal to its success state and surface it. @param {string} [email] */
  #showSuccess = (email) => {
    const formView = this.querySelector('.back-in-stock__form-view');
    const success = this.querySelector('.back-in-stock__success');
    if (formView) formView.hidden = true;
    if (success) {
      const body = success.querySelector('.back-in-stock__success-body');
      if (body) {
        body.textContent = email
          ? `Obvestili vas bomo na ${email}, takoj ko bo izdelek spet na zalogi.`
          : 'Obvestili vas bomo, takoj ko bo izdelek spet na zalogi.';
      }
      success.hidden = false;
    }

    const dialogComponent = this.querySelector('dialog-component');
    if (dialogComponent && typeof dialogComponent.showDialog === 'function') {
      dialogComponent.showDialog();
    }
  };

  /** @param {SubmitEvent} event */
  #handleSubmit = (event) => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement)) return;

    // Honeypot — a real user never fills this field.
    const honeypot = /** @type {HTMLInputElement | null} */ (form.querySelector('[name="contact[website]"]'));
    if (honeypot && honeypot.value.trim() !== '') {
      event.preventDefault();
      return;
    }

    const emailInput = /** @type {HTMLInputElement | null} */ (form.querySelector('input[name="contact[email]"]'));
    const email = emailInput ? emailInput.value.trim() : '';
    if (!emailInput || email === '' || !emailInput.checkValidity()) {
      event.preventDefault();
      this.#showError('Vnesite veljaven e-naslov.');
      emailInput?.focus();
      return;
    }
    this.#clearError();

    // Remember for the post-redirect return path (return_to -> ?bis_ok=1).
    try {
      sessionStorage.setItem(PENDING_VARIANT_KEY, this.#variantId);
      sessionStorage.setItem(PENDING_EMAIL_KEY, email);
    } catch (_e) {
      // ignore — the localStorage flag is set on return regardless.
    }

    // Processing state: disable the CTA + show feedback while the form navigates / hCaptcha runs.
    // (We do NOT preventDefault, so the native submit still proceeds.)
    const submitButton = /** @type {HTMLButtonElement | null} */ (form.querySelector('.back-in-stock__submit'));
    if (submitButton) {
      if (!submitButton.dataset.idleLabel) submitButton.dataset.idleLabel = submitButton.textContent.trim();
      submitButton.disabled = true;
      submitButton.setAttribute('aria-busy', 'true');
      submitButton.textContent = 'Pošiljam …';
      // Defensive reset if navigation never happens (e.g., the shopper dismisses the spam challenge).
      setTimeout(() => {
        submitButton.disabled = false;
        submitButton.removeAttribute('aria-busy');
        submitButton.textContent = submitButton.dataset.idleLabel || 'Prijavite me';
      }, 12000);
    }

    // The form submits natively: Shopify's spam protection (hCaptcha) runs during the real
    // navigation, then redirects to return_to (?bis_ok=1), where #handleReturn() confirms success.
    // A best-effort no-reload AJAX path is wired in here only after the dev-theme VERIFY step
    // confirms the exact success signal.
  };

  /**
   * Confirm an opt-in after Shopify redirected back from the contact-form POST.
   * Primary signal is our own `?bis_ok=1` (via the form's return_to); if Shopify ignores
   * return_to we fall back to its native `?contact_posted=true`. Both are gated by the
   * sessionStorage pending flag (set only by our own submit), so neither can false-positive.
   */
  #handleReturn = () => {
    const params = new URLSearchParams(location.search);
    const isReturn = params.has('bis_ok') || params.get('contact_posted') === 'true';
    if (!isReturn) return;

    let pendingVariant = '';
    let pendingEmail = '';
    try {
      pendingVariant = sessionStorage.getItem(PENDING_VARIANT_KEY) || '';
      pendingEmail = sessionStorage.getItem(PENDING_EMAIL_KEY) || '';
    } catch (_e) {
      // ignore
    }

    // Only treat as a genuine return if THIS browser just submitted (pending flag present).
    // A stray/shared visit to ?bis_ok=1 just gets the param stripped.
    if (pendingVariant) {
      this.#rememberOptIn(pendingVariant);
      this.#applyOptInState();
      this.#showSuccess(pendingEmail);
      try {
        sessionStorage.removeItem(PENDING_VARIANT_KEY);
        sessionStorage.removeItem(PENDING_EMAIL_KEY);
      } catch (_e) {
        // ignore
      }
    }

    params.delete('bis_ok');
    params.delete('contact_posted');
    const query = params.toString();
    history.replaceState({}, '', location.pathname + (query ? `?${query}` : '') + location.hash);
  };

  /** @param {import('@theme/events').VariantUpdateEvent} event */
  #onVariantUpdate = (event) => {
    const resource = event.detail?.resource;
    if (!resource) return;

    const newId = String(resource.id);
    if (newId === this.#variantId) return;

    // The re-rendered section is already in the event payload — reuse it instead of re-fetching.
    const html = event.detail?.data?.html;
    const source =
      html?.querySelector(`back-in-stock-optin[data-section-id="${this.dataset.sectionId}"]`) ||
      html?.querySelector('back-in-stock-optin');

    if (source) {
      // morph() brings over the `hidden` state + populated fields/chip for the new variant.
      morph(this, source);
      this.#applyOptInState();
    } else if (resource.available) {
      // New variant is in stock and not rendered -> hide.
      this.setAttribute('hidden', '');
    }
  };
}

if (!customElements.get('back-in-stock-optin')) {
  customElements.define('back-in-stock-optin', BackInStockOptin);
}
